import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../../components/feature/LanguageSwitcher';
import NewProjectsModal from '../../components/feature/NewProjectsModal';

const Home = () => {
  const { t } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNewProjectsOpen, setIsNewProjectsOpen] = useState(false);
  
  // Video states
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoStage, setVideoStage] = useState<'scanning' | 'intro' | 'stats' | 'mockup' | 'comparison'>('scanning');
  const [introStep, setIntroStep] = useState(0);
  const [mockupScroll, setMockupScroll] = useState(0);
  const [statsCounter, setStatsCounter] = useState({
    users: 0,
    countries: 0,
    posts: 0
  });

  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Video stage progression
  useEffect(() => {
    if (!isVideoPlaying) return;

    const timers: NodeJS.Timeout[] = [];

    // Stage 1: Scanning (3s)
    timers.push(setTimeout(() => {
      setVideoStage('intro');
    }, 3000));

    // Stage 2: Intro steps (2s each = 10s total)
    for (let i = 1; i <= 5; i++) {
      timers.push(setTimeout(() => {
        setIntroStep(i);
      }, 3000 + i * 2000));
    }

    // Stage 3: Stats (13s)
    timers.push(setTimeout(() => {
      setVideoStage('stats');
      // Animate counters
      let count = 0;
      const counterInterval = setInterval(() => {
        count += 1;
        setStatsCounter({
          users: Math.min(count * 50000, 2500000),
          countries: Math.min(count * 3, 150),
          posts: Math.min(count * 100000, 5000000)
        });
        if (count >= 50) clearInterval(counterInterval);
      }, 50);
      timers.push(counterInterval as any);
    }, 13000));

    // Stage 4: Mockup scroll (16s)
    timers.push(setTimeout(() => {
      setVideoStage('mockup');
      let scroll = 0;
      const scrollInterval = setInterval(() => {
        scroll += 2;
        setMockupScroll(scroll);
        if (scroll >= 200) {
          clearInterval(scrollInterval);
        }
      }, 50);
      timers.push(scrollInterval as any);
    }, 16000));

    // Stage 5: Comparison (26s)
    timers.push(setTimeout(() => {
      setVideoStage('comparison');
    }, 26000));

    // Loop back to start (36s)
    timers.push(setTimeout(() => {
      setVideoStage('scanning');
      setIntroStep(0);
      setMockupScroll(0);
      setStatsCounter({ users: 0, countries: 0, posts: 0 });
    }, 36000));

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [isVideoPlaying]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleVideoClose = () => {
    setIsVideoPlaying(false);
    setVideoStage('scanning');
    setIntroStep(0);
    setMockupScroll(0);
    setStatsCounter({ users: 0, countries: 0, posts: 0 });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/submit/facetrue-signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setName('');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const introSteps = [
    {
      icon: 'ri-shield-check-line',
      color: 'from-blue-500 to-cyan-500',
      title: t('step1_title'),
      subtitle: t('step1_subtitle')
    },
    {
      icon: 'ri-user-heart-line',
      color: 'from-purple-500 to-pink-500',
      title: t('step2_title'),
      subtitle: t('step2_subtitle')
    },
    {
      icon: 'ri-global-line',
      color: 'from-green-500 to-emerald-500',
      title: t('step3_title'),
      subtitle: t('step3_subtitle')
    },
    {
      icon: 'ri-shield-user-line',
      color: 'from-orange-500 to-red-500',
      title: t('step4_title'),
      subtitle: t('step4_subtitle')
    },
    {
      icon: 'ri-heart-3-line',
      color: 'from-pink-500 to-rose-500',
      title: t('step5_title'),
      subtitle: t('step5_subtitle')
    }
  ];

  const mockPosts = [
    {
      avatar: 'https://public.readdy.ai/ai/img_res/645da39638cf1746a132f072bb4ed3a0.jpg',
      name: 'Sarah Chen',
      verified: true,
      time: '2h',
      content: t('mock_post_1'),
      likes: '234',
      comments: '45'
    },
    {
      avatar: 'https://readdy.ai/api/search-image?query=professional%20man%20portrait%20verified%20user&width=100&height=100&seq=user2&orientation=squarish',
      name: 'Michael Rodriguez',
      verified: true,
      time: '4h',
      content: t('mock_post_2'),
      likes: '567',
      comments: '89'
    },
    {
      avatar: 'https://readdy.ai/api/search-image?query=professional%20woman%20portrait%20verified%20user&width=100&height=100&seq=user3&orientation=squarish',
      name: 'Emma Thompson',
      verified: true,
      time: '6h',
      content: t('mock_post_3'),
      likes: '892',
      comments: '123'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-gray-900/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
                alt="FaceTrue Logo" 
                className="w-10 h-10 rounded-full"
              />
              <span className="text-xl font-bold">{t('hero_title')}</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('features')} className="text-white/70 hover:text-white transition-colors text-sm font-medium cursor-pointer">
                {t('nav_features')}
              </button>
              <button onClick={() => scrollToSection('security')} className="text-white/70 hover:text-white transition-colors text-sm font-medium cursor-pointer">
                {t('nav_security')}
              </button>
              <button onClick={() => scrollToSection('community')} className="text-white/70 hover:text-white transition-colors text-sm font-medium cursor-pointer">
                {t('nav_community')}
              </button>
            </div>
            <div className="flex items-center space-x-4">
              {/* Upload Video Button */}
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/admin-upload')}
                className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap hover:scale-105"
              >
                <i className="ri-upload-cloud-line text-white text-lg"></i>
                <span className="text-white text-sm font-bold">Upload Video</span>
              </button>
              
              {/* New Project Button */}
              <button
                onClick={() => setIsNewProjectsOpen(true)}
                className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 rounded-full transition-all cursor-pointer shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-rocket-line text-white text-lg"></i>
                <span className="text-white text-sm font-bold">{t('nav_new_project')}</span>
              </button>
              <div className="hidden md:block">
                <LanguageSwitcher />
              </div>
              <div className="md:hidden">
                <LanguageSwitcher />
              </div>
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/auth')}
                className="bg-white text-black px-6 py-2 rounded-full font-semibold hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer hover:scale-105"
              >
                {t('nav_join')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/20 to-black"></div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto animate-fade-in">
          <div className="mb-12 animate-float">
            <img 
              src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
              alt="FaceTrue Logo" 
              className="w-32 h-32 mx-auto mb-8 rounded-full shadow-2xl shadow-white/20"
            />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-8 leading-tight animate-slide-up">
            <strong>{t('hero_title')}</strong><br />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent text-3xl md:text-4xl">
              <strong>{t('hero_subtitle')}</strong>
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white mb-12 leading-relaxed max-w-4xl mx-auto font-semibold animate-slide-up-delay">
            <strong className="text-white">{t('hero_description_1')}</strong><br />
            <strong className="text-white">{t('hero_description_2')}</strong><br />
            <strong className="text-white">{t('hero_description_3')}</strong>
          </p>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse-slow animation-delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse-slow animation-delay-2000"></div>
        </div>
      </section>

      {/* Age-Based Content Lanes */}
      <section id="features" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6"><strong>{t('age_title')}</strong></h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Children (Yellow) */}
            <a 
              href="/children"
              className="bg-gray-900/30 rounded-3xl p-8 border-l-4 border-yellow-400 hover:bg-gray-800/50 transition-all duration-500 block cursor-pointer hover:scale-105 hover:shadow-2xl hover:shadow-yellow-400/20 animate-fade-in-up"
            >
              <div className="relative">
                <div className="absolute top-0 right-0 bg-yellow-400/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-semibold">
                  {t('children_label')}
                </div>
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-emotion-happy-line text-2xl text-black"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-yellow-400"><strong>{t('children_title')}</strong></h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-yellow-400"></i>
                  <span className="text-white/80"><strong>{t('children_feature1')}</strong></span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-yellow-400"></i>
                  <span className="text-white/80"><strong>{t('children_feature2')}</strong></span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-yellow-400"></i>
                  <span className="text-white/80"><strong>{t('children_feature3')}</strong></span>
                </li>
              </ul>
            </a>

            {/* Adults (Blue) */}
            <a 
              href="/adults"
              className="bg-gray-900/30 rounded-3xl p-8 border-l-4 border-blue-400 hover:bg-gray-800/50 transition-all duration-500 md:scale-105 shadow-2xl shadow-blue-500/20 block cursor-pointer hover:scale-110 animate-fade-in-up animation-delay-200"
            >
              <div className="relative">
                <div className="absolute top-0 right-0 bg-blue-400/20 text-blue-400 px-3 py-1 rounded-full text-sm font-semibold">
                  {t('adults_label')}
                </div>
                <div className="w-16 h-16 bg-blue-400 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-3-line text-2xl text-white"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-blue-400"><strong>{t('adults_title')}</strong></h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-400"></i>
                  <span className="text-white/80"><strong>{t('adults_feature1')}</strong></span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-400"></i>
                  <span className="text-white/80"><strong>{t('adults_feature2')}</strong></span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-blue-400"></i>
                  <span className="text-white/80"><strong>{t('adults_feature3')}</strong></span>
                </li>
              </ul>
            </a>

            {/* Seniors (Green) */}
            <a 
              href="/seniors"
              className="bg-gray-900/30 rounded-3xl p-8 border-l-4 border-green-400 hover:bg-gray-800/50 transition-all duration-500 block cursor-pointer hover:scale-105 hover:shadow-2xl hover:shadow-green-400/20 animate-fade-in-up animation-delay-400"
            >
              <div className="relative">
                <div className="absolute top-0 right-0 bg-green-400/20 text-green-400 px-3 py-1 rounded-full text-sm font-semibold">
                  {t('seniors_label')}
                </div>
                <div className="w-16 h-16 bg-green-400 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-heart-line text-2xl text-white"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-green-400"><strong>{t('seniors_title')}</strong></h3>
              <ul className="space-y-3">
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-white/80"><strong>{t('seniors_feature1')}</strong></span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-white/80"><strong>{t('seniors_feature2')}</strong></span>
                </li>
                <li className="flex items-center space-x-3">
                  <i className="ri-check-line text-green-400"></i>
                  <span className="text-white/80"><strong>{t('seniors_feature3')}</strong></span>
                </li>
              </ul>
            </a>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section id="security" className="py-20 bg-gradient-to-b from-black to-gray-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in-left">
              <h2 className="text-4xl md:text-5xl font-bold mb-12 text-white"><strong>{t('why_title')}</strong></h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-line text-blue-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature1_title')}</strong></h3>
                    <p className="text-gray-300 font-medium">{t('why_feature1_desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-green-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature2_title')}</strong></h3>
                    <p className="text-gray-300 font-medium">{t('why_feature2_desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-forbid-line text-purple-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature3_title')}</strong></h3>
                    <p className="text-gray-300 font-medium">{t('why_feature3_desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-line text-yellow-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature4_title')}</strong></h3>
                    <p className="text-gray-300 font-medium">{t('why_feature4_desc')}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-red-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature5_title')}</strong></h3>
                    <p className="text-gray-300 font-medium">{t('why_feature5_desc')}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-flag-line text-orange-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature6_title')}</strong></h3>
                    <p className="text-gray-300 font-medium" dangerouslySetInnerHTML={{ __html: t('why_feature6_desc') }}></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-fill text-pink-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature7_title')}</strong></h3>
                    <p className="text-gray-300 font-medium" dangerouslySetInnerHTML={{ __html: t('why_feature7_desc') }}></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 hover:translate-x-2 transition-transform">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-advertisement-line text-cyan-400 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white"><strong>{t('why_feature8_title')}</strong></h3>
                    <p className="text-gray-300 font-medium" dangerouslySetInnerHTML={{ __html: t('why_feature8_desc') }}></p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative animate-fade-in-right">
              {/* Video Demo Frame */}
              <div className="relative w-full max-w-md mx-auto">
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-[3rem] p-4 shadow-2xl shadow-blue-500/20 border-8 border-gray-800">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-7 bg-gray-800 rounded-b-3xl z-10"></div>
                  
                  {/* Screen */}
                  <div className="relative bg-black rounded-[2.5rem] overflow-hidden aspect-[9/19.5]">
                    {/* Video Content */}
                    {!isVideoPlaying ? (
                      // Thumbnail State
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-purple-900/30 to-green-900/30">
                        <img 
                          src="https://readdy.ai/api/search-image?query=modern%20smartphone%20social%20media%20app%20interface%20showing%20AI%20face%20verification%20screen%20with%20glowing%20blue%20scan%20lines%20user%20profile%20with%20verified%20checkmark%20clean%20dark%20UI%20design%20futuristic%20technology%20aesthetic%20professional%20mobile%20app%20mockup&width=400&height=867&seq=facetrue-video-demo&orientation=portrait"
                          alt="FaceTrue Demo"
                          className="w-full h-full object-cover"
                        />
                        
                        {/* Play Button Overlay */}
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <button
                            onClick={() => setIsVideoPlaying(true)}
                            className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all shadow-2xl cursor-pointer group"
                          >
                            <i className="ri-play-fill text-4xl text-black ml-1"></i>
                          </button>
                        </div>
                        
                        {/* Video Info */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
                              <i className="ri-play-circle-line text-white text-xl"></i>
                            </div>
                            <div>
                              <h4 className="text-white font-bold text-sm">{t('video_title')}</h4>
                              <p className="text-gray-300 text-xs">{t('video_subtitle')}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-400">
                            <span className="flex items-center space-x-1">
                              <i className="ri-time-line"></i>
                              <span>2:30</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <i className="ri-eye-line"></i>
                              <span>{t('video_views', { count: 125000 })}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Playing State
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900">
                        {/* Simulated App Interface */}
                        <div className="relative w-full h-full flex flex-col">
                          {/* App Header */}
                          <div className="bg-gray-900/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between border-b border-white/10">
                            <div className="flex items-center space-x-2">
                              <img 
                                src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
                                alt="FaceTrue"
                                className="w-8 h-8 rounded-full"
                              />
                              <span className="text-white font-bold text-sm">{t('hero_title')}</span>
                            </div>
                            <button
                              onClick={handleVideoClose}
                              className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-500/30"
                            >
                              <i className="ri-close-line text-red-400 text-lg"></i>
                            </button>
                          </div>

                          {/* Main Content */}
                          <div className="flex-1 flex flex-col items-center justify-center p-6 overflow-hidden">
                            {videoStage === 'scanning' ? (
                              // Stage 1: Scanning
                              <div className="space-y-6 w-full animate-fade-in">
                                <div className="relative mx-auto">
                                  <div className="w-48 h-48 rounded-full border-4 border-blue-500 flex items-center justify-center animate-pulse">
                                    <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-500/20 to-green-500/20 flex items-center justify-center overflow-hidden">
                                      <img 
                                        src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/1464f3cfec7547dc307103880a89962b.jpeg"
                                        alt="User"
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  </div>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-full h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent animate-scan"></div>
                                  </div>
                                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-500 rounded-full px-4 py-1 flex items-center space-x-1 shadow-lg">
                                    <i className="ri-shield-check-fill text-white text-sm"></i>
                                    <span className="text-white text-xs font-bold">Verified</span>
                                  </div>
                                </div>

                                <div className="text-center space-y-2">
                                  <h3 className="text-white font-bold text-lg animate-pulse">{t('scanning_title')}</h3>
                                  <p className="text-gray-400 text-sm">{t('scanning_desc')}</p>
                                </div>

                                <div className="w-full space-y-3">
                                  <div className="flex items-center space-x-3 animate-fade-in">
                                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                      <i className="ri-check-line text-white text-sm"></i>
                                    </div>
                                    <span className="text-green-400 text-sm font-medium">{t('scanning_step1')}</span>
                                  </div>
                                  <div className="flex items-center space-x-3 animate-fade-in animation-delay-500">
                                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                      <i className="ri-check-line text-white text-sm"></i>
                                    </div>
                                    <span className="text-green-400 text-sm font-medium">{t('scanning_step2')}</span>
                                  </div>
                                  <div className="flex items-center space-x-3 animate-fade-in animation-delay-1000">
                                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center animate-spin">
                                      <i className="ri-loader-4-line text-white text-sm"></i>
                                    </div>
                                    <span className="text-blue-400 text-sm font-medium">{t('scanning_step3')}</span>
                                  </div>
                                </div>

                                <div className="mt-4 bg-green-500/20 border border-green-500/30 rounded-full px-6 py-2 animate-bounce-slow mx-auto">
                                  <span className="text-green-400 font-bold text-sm flex items-center space-x-2">
                                    <i className="ri-shield-check-line"></i>
                                    <span>{t('scanning_badge')}</span>
                                  </span>
                                </div>
                              </div>
                            ) : videoStage === 'intro' ? (
                              // Stage 2: Feature Intro
                              <div className="space-y-8 w-full animate-fade-in">
                                {introStep < 5 ? (
                                  <div className="text-center space-y-6">
                                    <div className={`w-32 h-32 bg-gradient-to-br ${introSteps[introStep].color} rounded-full flex items-center justify-center mx-auto shadow-2xl animate-scale-in`}>
                                      <i className={`${introSteps[introStep].icon} text-5xl text-white`}></i>
                                    </div>

                                    <div className="space-y-2 animate-slide-up">
                                      <h3 className="text-white font-bold text-2xl">
                                        {introSteps[introStep].title}
                                      </h3>
                                      <p className="text-gray-300 text-base">
                                        {introSteps[introStep].subtitle}
                                      </p>
                                    </div>

                                    <div className="flex items-center justify-center space-x-2 pt-4">
                                      {introSteps.map((_, index) => (
                                        <div
                                          key={index}
                                          className={`h-2 rounded-full transition-all ${
                                            index === introStep
                                              ? 'w-8 bg-white'
                                              : index < introStep
                                              ? 'w-2 bg-green-500'
                                              : 'w-2 bg-gray-600'
                                          }`}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            ) : videoStage === 'stats' ? (
                              // Stage 3: Statistics
                              <div className="space-y-8 w-full animate-fade-in">
                                <div className="text-center space-y-2 mb-8">
                                  <h3 className="text-white font-bold text-2xl">{t('stats_title')}</h3>
                                  <p className="text-gray-400 text-sm">{t('stats_subtitle')}</p>
                                </div>

                                <div className="space-y-6">
                                  {/* Users Counter */}
                                  <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl p-6 border border-blue-500/30">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                                          <i className="ri-user-line text-white text-xl"></i>
                                        </div>
                                        <div>
                                          <p className="text-gray-400 text-xs">{t('stats_users')}</p>
                                          <p className="text-white font-bold text-2xl">{(statsCounter.users / 1000000).toFixed(1)}M+</p>
                                        </div>
                                      </div>
                                      <div className="text-green-400 text-sm font-bold">+12%</div>
                                    </div>
                                  </div>

                                  {/* Countries Counter */}
                                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl p-6 border border-purple-500/30">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                                          <i className="ri-global-line text-white text-xl"></i>
                                        </div>
                                        <div>
                                          <p className="text-gray-400 text-xs">{t('stats_countries')}</p>
                                          <p className="text-white font-bold text-2xl">{statsCounter.countries}</p>
                                        </div>
                                      </div>
                                      <div className="text-green-400 text-sm font-bold">+8%</div>
                                    </div>
                                  </div>

                                  {/* Posts Counter */}
                                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl p-6 border border-green-500/30">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                                          <i className="ri-heart-line text-white text-xl"></i>
                                        </div>
                                        <div>
                                          <p className="text-gray-400 text-xs">{t('stats_posts')}</p>
                                          <p className="text-white font-bold text-2xl">{(statsCounter.posts / 1000000).toFixed(1)}M+</p>
                                        </div>
                                      </div>
                                      <div className="text-green-400 text-sm font-bold">+25%</div>
                                    </div>
                                  </div>
                                </div>

                                <div className="text-center pt-4">
                                  <div className="inline-flex items-center space-x-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full px-4 py-2">
                                    <i className="ri-star-fill text-yellow-400"></i>
                                    <span className="text-yellow-400 font-bold text-sm">{t('stats_badge')}</span>
                                  </div>
                                </div>
                              </div>
                            ) : videoStage === 'mockup' ? (
                              // Stage 4: App Mockup
                              <div className="w-full h-full animate-fade-in">
                                <div className="h-full overflow-hidden">
                                  <div 
                                    className="space-y-4 transition-transform duration-300"
                                    style={{ transform: `translateY(-${mockupScroll}px)` }}
                                  >
                                    {mockPosts.map((post, index) => (
                                      <div key={index} className="bg-gray-800/50 rounded-2xl p-4 border border-white/10">
                                        <div className="flex items-center space-x-3 mb-3">
                                          <img 
                                            src={post.avatar}
                                            alt={post.name}
                                            className="w-10 h-10 rounded-full object-cover"
                                          />
                                          <div className="flex-1">
                                            <div className="flex items-center space-x-1">
                                              <span className="text-white font-bold text-sm">{post.name}</span>
                                              {post.verified && (
                                                <i className="ri-verified-badge-fill text-blue-400 text-sm"></i>
                                              )}
                                            </div>
                                            <span className="text-gray-400 text-xs">{post.time}</span>
                                          </div>
                                        </div>
                                        <p className="text-white text-sm mb-3">{post.content}</p>
                                        <div className="flex items-center space-x-4 text-gray-400 text-xs">
                                          <button className="flex items-center space-x-1 hover:text-pink-400 transition-colors cursor-pointer">
                                            <i className="ri-heart-line"></i>
                                            <span>{post.likes}</span>
                                          </button>
                                          <button className="flex items-center space-x-1 hover:text-blue-400 transition-colors cursor-pointer">
                                            <i className="ri-chat-3-line"></i>
                                            <span>{post.comments}</span>
                                          </button>
                                          <button className="flex items-center space-x-1 hover:text-green-400 transition-colors cursor-pointer">
                                            <i className="ri-share-line"></i>
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ) : videoStage === 'comparison' ? (
                              // Stage 5: Before/After Comparison
                              <div className="space-y-6 w-full animate-fade-in">
                                <div className="text-center mb-6">
                                  <h3 className="text-white font-bold text-xl">{t('comparison_title')}</h3>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  {/* Before - Old Social Media */}
                                  <div className="space-y-3">
                                    <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4">
                                      <div className="text-center mb-3">
                                        <i className="ri-close-circle-line text-red-400 text-3xl"></i>
                                        <p className="text-red-400 font-bold text-xs mt-2">{t('comparison_old')}</p>
                                      </div>
                                      <div className="space-y-2 text-xs text-gray-400">
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-close-line text-red-400"></i>
                                          <span>{t('comparison_old_1')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-close-line text-red-400"></i>
                                          <span>{t('comparison_old_2')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-close-line text-red-400"></i>
                                          <span>{t('comparison_old_3')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-close-line text-red-400"></i>
                                          <span>{t('comparison_old_4')}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* After - FaceTrue */}
                                  <div className="space-y-3">
                                    <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4">
                                      <div className="text-center mb-3">
                                        <i className="ri-checkbox-circle-line text-green-400 text-3xl"></i>
                                        <p className="text-green-400 font-bold text-xs mt-2">{t('comparison_new')}</p>
                                      </div>
                                      <div className="space-y-2 text-xs text-gray-300">
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-check-line text-green-400"></i>
                                          <span>{t('comparison_new_1')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-check-line text-green-400"></i>
                                          <span>{t('comparison_new_2')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-check-line text-green-400"></i>
                                          <span>{t('comparison_new_3')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <i className="ri-check-line text-green-400"></i>
                                          <span>{t('comparison_new_4')}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* CTA */}
                                <div className="text-center pt-4">
                                  <button
                                    onClick={() => {
                                      handleVideoClose();
                                      scrollToSection('signup');
                                    }}
                                    className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-6 py-3 rounded-full font-bold text-sm hover:scale-105 transition-all shadow-lg cursor-pointer"
                                  >
                                    {t('cta_button')}
                                  </button>
                                </div>
                              </div>
                            ) : null}
                          </div>

                          {/* Bottom Info */}
                          <div className="bg-gray-900/90 backdrop-blur-sm px-4 py-3 border-t border-white/10">
                            <div className="flex items-center justify-between text-xs text-gray-400">
                              <span className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                                <span>{t('video_demo_live')}</span>
                              </span>
                              <span>{t('video_close')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Home Button */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-700 rounded-full"></div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-6 -left-6 w-16 h-16 bg-blue-500/20 rounded-full blur-xl animate-pulse"></div>
                <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-green-500/20 rounded-full blur-xl animate-pulse animation-delay-1000"></div>
              </div>
              
              {/* Video Description */}
              <div className="mt-8 text-center">
                <p className="text-gray-400 text-sm mb-2">
                  <i className="ri-information-line text-blue-400"></i> {isVideoPlaying ? t('video_info') : t('video_info_cta')}
                </p>
                <p className="text-gray-500 text-xs">
                  {t('video_update')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="signup" className="py-20 bg-black">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-12 border border-white/10 relative overflow-hidden animate-fade-in-up hover:scale-105 transition-transform duration-500">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-green-500/10"></div>
            <div className="relative z-10">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4"><strong>{t('cta_title')}</strong></h2>
                <p className="text-xl text-white/60">{t('cta_subtitle')}</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('cta_name')}
                    className="w-full bg-gray-800/50 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('cta_email')}
                    className="w-full bg-gray-800/50 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-white/40 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    required
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 rounded-xl font-bold text-lg hover:scale-105 transition-all shadow-2xl shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>{t('cta_submitting')}</span>
                    </>
                  ) : (
                    <>
                      <span>{t('cta_button')}</span>
                      <i className="ri-arrow-right-line"></i>
                    </>
                  )}
                </button>
                
                {submitStatus === 'success' && (
                  <div className="text-green-400 text-center font-semibold animate-fade-in">
                    {t('cta_success')}
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="text-red-400 text-center font-semibold animate-fade-in">
                    {t('cta_error')}
                  </div>
                )}
              </form>
              
              <p className="text-center text-white/40 text-sm mt-6">
                {t('cta_privacy')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="community" className="py-16 bg-gray-900 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img 
            src="https://static.readdy.ai/image/a79563fe53efb1b307a7324e5db6bc2e/126dd8573ffd64afbaee10d6ee258da0.jpeg"
            alt="FaceTrue Logo" 
            className="w-8 h-8 mx-auto mb-4 rounded-full"
          />
          <p className="text-white/50 text-sm mb-2">
            {t('footer_copyright')}
          </p>
          <p className="text-white/30 text-sm mb-4">
            {t('footer_creator')}
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            className="text-white/40 text-xs hover:text-white/60 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('footer_powered')}
          </a>
        </div>
      </footer>

      {/* New Projects Modal */}
      <NewProjectsModal 
        isOpen={isNewProjectsOpen} 
        onClose={() => setIsNewProjectsOpen(false)} 
      />
    </div>
  );
};

export default Home;
